export type CharacterKind =
  | 'man'
  | 'woman'
  | 'elderly'
  | 'young'
  | 'courier'
  | 'office'
  | 'tourist'
  | 'fisher'
  | 'photographer'
  | 'raincoat'
  | 'cat'
  | 'dog'
  | 'panda'
  | 'duck'
  | 'pig'
  | 'sheep'
  | 'rabbit'
  | 'otter'
  | 'shiba'
  | 'orangeCat';

export interface CharacterDefinition {
  kind: CharacterKind;
  name: string;
  animal: boolean;
  weight: number;
  swimSkill: number;
  panicLevel: number;
  stamina: number;
  grabSkill: number;
  vestSkill: number;
  impactRisk: number;
  ropeRisk: number;
  driftFactor: number;
  color: number;
  bestTools: string[];
  quip: string;
}

const human = (
  kind: CharacterKind,
  name: string,
  color: number,
  changes: Partial<CharacterDefinition> = {},
): CharacterDefinition => ({
  kind,
  name,
  animal: false,
  weight: 70,
  swimSkill: 0.45,
  panicLevel: 0.5,
  stamina: 0.82,
  grabSkill: 0.7,
  vestSkill: 0.68,
  impactRisk: 0.18,
  ropeRisk: 0.15,
  driftFactor: 1,
  color,
  bestTools: ['lifeRing', 'rescuePole'],
  quip: '这位落水者很努力，你也最好努力一点。',
  ...changes,
});

const animal = (
  kind: CharacterKind,
  name: string,
  color: number,
  changes: Partial<CharacterDefinition> = {},
): CharacterDefinition => ({
  kind,
  name,
  animal: true,
  weight: 18,
  swimSkill: 0.68,
  panicLevel: 0.42,
  stamina: 0.9,
  grabSkill: 0.62,
  vestSkill: 0.35,
  impactRisk: 0.15,
  ropeRisk: 0.1,
  driftFactor: 0.82,
  color,
  bestTools: ['rescuePole', 'lifeRing'],
  quip: '它会游泳，但它不保证配合你。',
  ...changes,
});

export const CHARACTER_TYPES: Record<CharacterKind, CharacterDefinition> = {
  man: human('man', '散步大叔', 0x49a3e3),
  woman: human('woman', '拍照女士', 0xe7659a, { panicLevel: 0.55 }),
  elderly: human('elderly', '晨练老人', 0x8f80c7, {
    weight: 62,
    swimSkill: 0.15,
    panicLevel: 0.7,
    ropeRisk: 0.28,
    stamina: 0.7,
  }),
  young: human('young', '追风少年', 0x41b883, { swimSkill: 0.58, stamina: 0.94 }),
  courier: human('courier', '快递员', 0x3d78cb, { weight: 76, bestTools: ['lifeRing', 'rope'] }),
  office: human('office', '上班族', 0x5c6687, { swimSkill: 0.3 }),
  tourist: human('tourist', '游客', 0xffa34e, { panicLevel: 0.68 }),
  fisher: human('fisher', '钓鱼者', 0x3b8f85, { swimSkill: 0.6, bestTools: ['rope', 'lifeRing'] }),
  photographer: human('photographer', '摄影师', 0x7f66bd, { weight: 66 }),
  raincoat: human('raincoat', '雨衣人', 0xffcf40, { vestSkill: 0.55 }),
  cat: animal('cat', '黑猫', 0x343c4d, {
    weight: 6,
    swimSkill: 0.55,
    panicLevel: 0.9,
    ropeRisk: 0.2,
    quip: '猫会游，但它不想让任何人知道。',
  }),
  dog: animal('dog', '快乐小狗', 0xbf7d42, { weight: 24, swimSkill: 0.75, grabSkill: 0.78 }),
  panda: animal('panda', '圆滚滚熊猫', 0xf5f2e8, {
    weight: 104,
    swimSkill: 0.2,
    panicLevel: 0.55,
    bestTools: ['inflatableBoat', 'rope'],
    quip: '熊猫很可爱，也确实很重。',
  }),
  duck: animal('duck', '淡定鸭子', 0xffd148, {
    weight: 3,
    swimSkill: 0.98,
    panicLevel: 0.1,
    grabSkill: 0.2,
    ropeRisk: 0.02,
    bestTools: ['observe'],
    quip: '它好像根本不需要你救。',
  }),
  pig: animal('pig', '粉红小猪', 0xf18c9d, { weight: 32, swimSkill: 0.38 }),
  sheep: animal('sheep', '卷毛绵羊', 0xf0ead7, { weight: 48, swimSkill: 0.25, panicLevel: 0.65 }),
  rabbit: animal('rabbit', '长耳兔', 0xd8c7b7, { weight: 5, swimSkill: 0.42, panicLevel: 0.82 }),
  otter: animal('otter', '专业水獭', 0x855d43, {
    weight: 12,
    swimSkill: 0.96,
    panicLevel: 0.08,
    bestTools: ['observe'],
    quip: '它游得比你的救援计划更专业。',
  }),
  shiba: animal('shiba', '柴犬', 0xd98a42, { weight: 13, swimSkill: 0.72, grabSkill: 0.72 }),
  orangeCat: animal('orangeCat', '落水橘猫', 0xf1933a, {
    weight: 7,
    swimSkill: 0.5,
    panicLevel: 0.92,
    bestTools: ['rescuePole'],
    quip: '它获救以后大概也不会感谢你。',
  }),
};

export const CHARACTER_ORDER = Object.keys(CHARACTER_TYPES) as CharacterKind[];
